import { useEffect, useRef } from "react";
import { toast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { FINISHED_STATUSES, UseBatchImportReturn } from "./useBatchImport";

/**
 * Notification bottom-right (toast) qui prend le relais quand la modale d'import
 * est fermée pendant qu'un batch tourne : barre de progression pendant l'import,
 * puis récap succinct une fois terminé. Rendu tant que le menu est monté.
 */
export function BatchImportProgressToast({
  batch,
  open,
  onOpen,
}: {
  batch: UseBatchImportReturn;
  open: boolean;
  onOpen: () => void;
}) {
  const vt = useVisibleT();
  const { items, running, done } = batch;
  const toastRef = useRef<ReturnType<typeof toast> | null>(null);

  const total = items.length;
  const finished = items.filter((i) =>
    FINISHED_STATUSES.includes(i.status),
  ).length;
  const percent = total ? Math.round((finished / total) * 100) : 0;
  const success = items.filter((i) => i.status === "success").length;
  const failed = items.filter(
    (i) => i.status === "error" || i.status === "skipped",
  ).length;

  useEffect(() => {
    // La notif ne s'affiche que lorsque la modale est fermée.
    if (open || total === 0) {
      toastRef.current?.dismiss();
      toastRef.current = null;
      return;
    }

    const title = vt("bo.batchImport.title", "Importer des images en lot");

    const description = running ? (
      <div className="flex w-60 flex-col gap-2">
        <div className="flex items-center justify-between text-sm">
          <span>{vt("bo.batchImport.toastImporting", "Import en cours…")}</span>
          <span className="tabular-nums">
            {finished}/{total}
          </span>
        </div>
        <Progress value={percent} />
        <Button
          variant="link"
          size="sm"
          className="h-auto self-start p-0 text-xs"
          onClick={onOpen}
        >
          {vt("bo.batchImport.toastOpen", "Afficher")}
        </Button>
      </div>
    ) : (
      <div className="flex w-60 flex-col gap-2">
        <span className="text-sm">
          {success} {vt("bo.batchImport.toastCreated", "fiches créées")}
          {failed > 0
            ? ` · ${failed} ${vt("bo.batchImport.toastFailed", "échecs")}`
            : ""}
        </span>
        <Button
          variant="link"
          size="sm"
          className="h-auto self-start p-0 text-xs"
          onClick={onOpen}
        >
          {vt("bo.batchImport.toastSeeRecap", "Voir le récap")}
        </Button>
      </div>
    );

    const payload = {
      title,
      description,
      duration: running ? 600_000 : 15_000,
    };

    if (toastRef.current) {
      toastRef.current.update({ ...payload, id: toastRef.current.id });
    } else {
      toastRef.current = toast(payload);
    }
  }, [open, total, running, done, finished, percent, success, failed, vt, onOpen]);

  // Nettoyage : dismiss du toast au démontage.
  useEffect(
    () => () => {
      toastRef.current?.dismiss();
    },
    [],
  );

  return null;
}

export default BatchImportProgressToast;
